import type { PortalFaq } from "@/actions/faq/fetchPortalFaqs";

export const DEFAULT_FAQ_CATEGORIES = [
  "general",
  "order",
  "costs",
  "design",
  "company",
  "affiliate",
] as const;

export type DefaultPortalFaqCategory = (typeof DEFAULT_FAQ_CATEGORIES)[number];
export type PortalFaqCategory = string;
export type PortalFaqCategoryLabels = Pick<
  PortalFaq,
  "category_de" | "category_en"
>;

const DEFAULT_CATEGORY_ORDER = new Map(
  DEFAULT_FAQ_CATEGORIES.map((category, index) => [category, index]),
);

export const isDefaultFaqCategory = (
  category: string,
): category is DefaultPortalFaqCategory =>
  DEFAULT_CATEGORY_ORDER.has(category as DefaultPortalFaqCategory);

export const getFaqCategories = (managedFaqs: PortalFaq[]) => {
  const managedCategories = managedFaqs
    .map((faq) => faq.category)
    .filter((category) => category.trim().length > 0);

  return Array.from(
    new Set([...DEFAULT_FAQ_CATEGORIES, ...managedCategories]),
  ).sort(
    (left, right) =>
      (DEFAULT_CATEGORY_ORDER.get(left as DefaultPortalFaqCategory) ??
        DEFAULT_FAQ_CATEGORIES.length) -
        (DEFAULT_CATEGORY_ORDER.get(right as DefaultPortalFaqCategory) ??
          DEFAULT_FAQ_CATEGORIES.length) || left.localeCompare(right),
  );
};

export const getFaqCategoryLabel = (
  category: string,
  tCommon: (key: string) => string,
  labels?: PortalFaqCategoryLabels,
  locale = "de",
) => {
  const localized = locale.toLowerCase().startsWith("en")
    ? labels?.category_en
    : labels?.category_de;

  if (localized?.trim()) {
    return localized.trim();
  }

  return isDefaultFaqCategory(category) ? tCommon(category) : category;
};

export const getFaqCategoryLabels = (
  managedFaqs: PortalFaq[],
  category: string,
): PortalFaqCategoryLabels | undefined =>
  managedFaqs.find((faq) => faq.category === category);
